import { useMemo } from "react"
import z from "zod"

import {
  DateFilterOperator,
  SlidingPreset,
} from "@tfg/types/types/date-filter.dto"

export type SlidingPresetSelectItem = {
  label: string
  value: SlidingPreset
  key: SlidingPreset
}

export const useGetSlidingPresetForOperator = (
  operator: DateFilterOperator
): SlidingPreset[] => {
  const mapping = useMemo<Map<DateFilterOperator[], SlidingPreset[]>>(() => {
    return new Map<DateFilterOperator[], SlidingPreset[]>()
      .set(
        [DateFilterOperator.BETWEEN, DateFilterOperator.IS_NOT_BETWEEN],
        [SlidingPreset.CUSTOM]
      )
      .set(
        [
          DateFilterOperator.EQUALS,
          DateFilterOperator.NOT_EQUALS,
          DateFilterOperator.BEFORE,
          DateFilterOperator.AFTER,
          DateFilterOperator.IS_BEFORE_OR_EQUALS,
          DateFilterOperator.IS_AFTER_OR_EQUALS,
        ],
        z.array(z.enum(SlidingPreset)).parse(Object.values(SlidingPreset))
      )
      .set([DateFilterOperator.IS_EMPTY, DateFilterOperator.IS_NOT_EMPTY], [])
  }, [])

  const presets = useMemo((): SlidingPreset[] => {
    const key = Array.from(mapping.keys()).find((keys) =>
      keys.includes(operator)
    )
    return key ? (mapping.get(key) ?? []) : []
  }, [mapping, operator])

  return presets
}

export const useSlidingPresetsSelectItems = (
  labelTranslationResolver: Record<SlidingPreset, string>,
  operator: DateFilterOperator
): SlidingPresetSelectItem[] => {
  const presets = useGetSlidingPresetForOperator(operator)

  return useMemo(() => {
    return presets.map(
      (operator: SlidingPreset): SlidingPresetSelectItem => ({
        label: labelTranslationResolver[operator] ?? "",
        value: operator,
        key: operator,
      })
    )
  }, [labelTranslationResolver, presets])
}
